(() => {
  const VERSION = '20260519-cart-close-fix-v32-v16';
  const PURPLE = '#6b4f9a';
  const PURPLE_DARK = '#563a82';
  const CREAM = '#fdfbf5';
  const INK = '#102033';
  const GREY = '#e4e2e8';
  const THRESHOLD_CENTS = 3499;
  const LP_GIFT_RETAIL_CENTS = {
    shipping: 699,
    guide: 2499,
    steamers7: 1499,
  };
  const MAGNESIUM_BASE_VARIANT_ID = 51571881083159;
  const MAGNESIUM_BASE_SKU = 'ML-4-LAV';
  const LEGACY_LP_VARIANT_IDS = new Set([
    53184013205783,
    53184013238551,
    53184013271319,
  ]);
  const UPSELL_HANDLE = 'shower-steamers';
  const UPSELL_VARIANT_ID = '51571979518231';
  const UPSELL_FALLBACK = {
    title: 'Deep Sleep Shower Steamers',
    handle: UPSELL_HANDLE,
    description: 'A calming shower steamer made for an easier nighttime wind-down routine.',
    featured_image: 'https://cdn.shopify.com/s/files/1/0966/1943/0167/files/1_Dec15.jpg?v=1773098731',
    images: ['https://cdn.shopify.com/s/files/1/0966/1943/0167/files/1_Dec15.jpg?v=1773098731'],
    variants: [{ id: UPSELL_VARIANT_ID, price: 2499 }],
  };

  let lastCart = null;
  let lastCartAt = 0;
  let cartPromise = null;
  let productPromise = null;
  let applyQueued = false;
  let lastProgressTotal = 0;
  let legacyLpNormalizePromise = null;
  let freeShippingCleanupPromise = null;

  if (window.SoliniaRechargeCartOverridesBooted) return;
  window.SoliniaRechargeCartOverridesBooted = true;

  const money = (cents) => `$${(Math.max(0, cents) / 100).toFixed(2)}`;
  const text = (el) => (el?.textContent || '').replace(/\s+/g, ' ').trim();
  const assetUrl = (filename) => {
    const script = document.currentScript ||
      [...document.scripts].find((item) => /solinia-recharge-cart-overrides/i.test(item.src || ''));
    if (script?.src) return new URL(filename, script.src).toString();
    return `/cdn/shop/t/11/assets/${filename}`;
  };
  const CART_GIFT_THUMBNAILS = {
    guide: assetUrl('solinia-cart-sleep-hacks-free.jpg'),
    steamers7: assetUrl('solinia-cart-shower-steamers-free.jpg'),
  };
  const isLegacyLpVariantId = (id) => LEGACY_LP_VARIANT_IDS.has(Number(id));
  const isLegacyLpLine = (item) => {
    if (!item) return false;
    return isLegacyLpVariantId(item.id || item.variant_id) ||
      /^ML-4-LAV-LP-/.test(item.sku || '') ||
      /^LP \d+ Jar Offer$/i.test(item.variant_title || '') ||
      /Magnesium Sleep Cream\s*-\s*LP \d+ Jar Offer/i.test(item.title || '');
  };
  const getLineProperty = (item, key) => {
    const properties = item?.properties;
    if (!properties) return '';
    if (Array.isArray(properties)) {
      const pair = properties.find((property) => property?.[0] === key || property?.name === key);
      return pair?.[1] || pair?.value || '';
    }
    return properties[key] || '';
  };
  const isFreeShippingCreditItem = (item) =>
    (item?.sku || '') === 'LP-FREE-SHIPPING' ||
    /^Free Shipping$/i.test(item?.product_title || item?.title || '') ||
    /Free Shipping/i.test(getLineProperty(item, '_Solinia LP gift'));
  const isLpGiftItem = (item) =>
    isFreeShippingCreditItem(item) ||
    Boolean(getLineProperty(item, '_Solinia LP gift'));
  const isShowerSteamerLine = (item) => {
    const haystack = [
      item?.sku,
      item?.title,
      item?.product_title,
      item?.variant_title,
      getLineProperty(item, '_Solinia LP gift'),
    ].filter(Boolean).join(' ');
    return /(?:deep\s+sleep\s+)?shower\s+steamers?|steamers?|shower/i.test(haystack) ||
      /^SS[-_]/i.test(String(item?.sku || ''));
  };
  const cartHasShowerSteamers = (cart) =>
    (cart?.items || []).some(isShowerSteamerLine);
  const getShippingEligibleCartCents = (cart) =>
    (cart?.items || []).reduce((sum, item) => {
      if (isLpGiftItem(item)) return sum;
      return sum + Number(item.final_line_price ?? item.line_price ?? 0);
    }, 0);

  function normalizeLpGiftRetailProperties(item) {
    if (!item?.properties) return item;
    const giftName = item.properties['_Solinia LP gift'] || '';
    if (!/The Ultimate Sleep Hacks Guide/i.test(giftName)) return item;
    return {
      ...item,
      properties: {
        ...item.properties,
        '_Solinia gift retail': '$24.99',
      },
    };
  }

  function normalizeCartAddBody(body) {
    if (!body) return body;
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      if (isLegacyLpVariantId(body.get('id'))) body.set('id', String(MAGNESIUM_BASE_VARIANT_ID));
      return body;
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      if (isLegacyLpVariantId(body.get('id'))) body.set('id', String(MAGNESIUM_BASE_VARIANT_ID));
      return body;
    }
    if (typeof body !== 'string') return body;
    try {
      const parsed = JSON.parse(body);
      if (isLegacyLpVariantId(parsed.id)) parsed.id = MAGNESIUM_BASE_VARIANT_ID;
      if (Array.isArray(parsed.items)) {
        parsed.items = parsed.items.map((item) => {
          if (!item) return item;
          const normalizedGift = normalizeLpGiftRetailProperties(item);
          if (!isLegacyLpVariantId(normalizedGift.id)) return normalizedGift;
          return { ...normalizedGift, id: MAGNESIUM_BASE_VARIANT_ID };
        });
      }
      return JSON.stringify(parsed);
    } catch {
      return body;
    }
  }

  function patchFetchForLegacyLpVariants() {
    if (window.SoliniaLegacyLpFetchPatched || typeof window.fetch !== 'function') return;
    window.SoliniaLegacyLpFetchPatched = true;
    const nativeFetch = window.fetch.bind(window);
    window.fetch = (input, init = {}) => {
      const url = typeof input === 'string' ? input : input?.url || '';
      const mutatesCart = /\/cart\/(?:add|change|update|clear)(?:\.js)?(?:\?|$)/.test(url);
      if (/\/cart\/add(?:\.js)?(?:\?|$)/.test(url)) {
        return nativeFetch(input, {
          ...init,
          body: normalizeCartAddBody(init.body),
        }).then((response) => {
          lastCart = null;
          lastCartAt = 0;
          window.setTimeout(queueApply, 120);
          return response;
        });
      }
      return nativeFetch(input, init).then((response) => {
        if (mutatesCart) {
          lastCart = null;
          lastCartAt = 0;
          window.setTimeout(queueApply, 120);
        }
        return response;
      });
    };
  }
  const removeStorefrontLocalization = () => {
    [
      '.desktop-localization-wrapper',
      '.footer__localization',
      '#HeaderCountryForm',
      '#HeaderLanguageForm',
      '#FooterCountryForm',
      '#FooterLanguageForm',
      'localization-form',
      '#shopify-pc__banner',
      '.shopify-pc__banner',
      '.shopify-pc__banner__dialog',
      '.shopify-pc__prefs',
      'shopify-privacy-banner',
      '[data-shopify-privacy-banner]',
      '[id*="shopify-pc"]',
      '[class*="shopify-pc"]'
    ].forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => element.remove());
    });
  };
  const isVisible = (el) => {
    if (!el || !el.isConnected) return false;
    const rect = el.getBoundingClientRect();
    const style = window.getComputedStyle(el);
    return (rect.width > 0 || rect.height > 0 || el.getClientRects().length > 0) &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0';
  };
  const isOnscreen = (el) => {
    if (!isVisible(el)) return false;
    const rect = el.getBoundingClientRect();
    return rect.bottom > 0 &&
      rect.top < window.innerHeight &&
      rect.right > 0 &&
      rect.left < window.innerWidth;
  };

  function patchLpSleepGuideGiftValue() {
    document.querySelectorAll('.lp-product__gift').forEach((gift) => {
      if (!/Sleep Hacks Guide/i.test(text(gift))) return;
      gift.setAttribute('data-gift-value', '24.99');
      const strike = gift.querySelector('.lp-product__gift-pill s');
      if (strike) strike.textContent = '$24.99';
    });
    const checked = document.querySelector('.lp-product .lp-product__variant-input:checked');
    if (checked) checked.dispatchEvent(new Event('change', { bubbles: true }));
  }

  removeStorefrontLocalization();
  patchLpSleepGuideGiftValue();
  patchFetchForLegacyLpVariants();
  document.addEventListener('DOMContentLoaded', removeStorefrontLocalization);
  document.addEventListener('DOMContentLoaded', patchLpSleepGuideGiftValue);
  window.setTimeout(removeStorefrontLocalization, 500);
  window.setTimeout(removeStorefrontLocalization, 1500);
  window.setTimeout(patchLpSleepGuideGiftValue, 500);
  window.setTimeout(patchLpSleepGuideGiftValue, 1500);

  function getOpenShadowRoots(root = document) {
    const roots = [];
    root.querySelectorAll?.('*').forEach((el) => {
      if (!el.shadowRoot) return;
      roots.push(el.shadowRoot);
      roots.push(...getOpenShadowRoots(el.shadowRoot));
    });
    return roots;
  }

  function queryAllDeep(selector, root = document) {
    const roots = [root, ...getOpenShadowRoots(root)];
    return [...new Set(roots.flatMap((searchRoot) => [...(searchRoot.querySelectorAll?.(selector) || [])]))];
  }

  function getEventMatch(event, selector) {
    return event.composedPath?.().find((node) => node?.matches?.(selector)) || event.target.closest?.(selector);
  }

  function injectStyles() {
    if (document.getElementById('solinia-recharge-cart-overrides-style')) return;
    const style = document.createElement('style');
    style.id = 'solinia-recharge-cart-overrides-style';
    style.textContent = `
      .solinia-recharge-progress {
        display: block;
        width: 100%;
        box-sizing: border-box;
        flex: 0 0 100%;
        padding: 20px 24px 22px;
        border-bottom: 1px solid rgba(107, 79, 154, 0.16);
        background: ${CREAM};
      }
      .solinia-recharge-progress__text {
        margin: 0 0 14px;
        color: #4c3b75;
        font-family: "New Atten", var(--font-body-family, sans-serif);
        font-size: 15px;
        line-height: 1.3;
        text-align: center;
        letter-spacing: 0;
      }
      .solinia-recharge-progress__track {
        height: 10px;
        overflow: hidden;
        border-radius: 999px;
        background: #ece7ef;
      }
      .solinia-recharge-progress__track span {
        display: block;
        height: 100%;
        min-width: 10px;
        border-radius: inherit;
        background: ${PURPLE};
        transition: width 180ms ease;
      }
      .solinia-lp-free-shipping-line {
        display: grid;
        grid-template-columns: 76px 1fr auto;
        align-items: center;
        gap: 16px;
        padding: 18px 24px;
        border-bottom: 1px solid rgba(107, 79, 154, 0.14);
        background: #fff;
        color: ${INK};
        font-family: "New Atten", var(--font-body-family, sans-serif);
        letter-spacing: 0;
      }
      .solinia-lp-free-shipping-line__image {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 66px;
        height: 66px;
        border-radius: 7px;
        background: #f6f2fa;
        color: ${PURPLE};
        font-size: 30px;
      }
      .solinia-lp-free-shipping-line__title {
        margin: 0;
        color: ${INK};
        font-family: "Fields", var(--font-heading-family, serif);
        font-size: 17px;
        font-weight: 600;
        line-height: 1.1;
      }
      .solinia-lp-free-shipping-line__price {
        display: inline-flex;
        gap: 8px;
        align-items: baseline;
        color: ${PURPLE};
        font-family: "New Atten", var(--font-body-family, sans-serif);
        font-size: 16px;
        line-height: 1;
        white-space: nowrap;
      }
      .solinia-lp-free-shipping-line__price s,
      .solinia-lp-gift-price s {
        color: ${INK};
        opacity: 0.75;
        text-decoration: line-through;
      }
      .solinia-lp-free-shipping-line__price strong,
      .solinia-lp-gift-price strong {
        color: ${PURPLE};
        font-weight: 700;
      }
      .solinia-lp-gift-price {
        display: inline-flex;
        align-items: baseline;
        gap: 8px;
        color: ${PURPLE};
        font-family: "New Atten", var(--font-body-family, sans-serif);
        letter-spacing: 0;
        white-space: nowrap;
      }
      .solinia-recharge-info-button {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        min-height: 36px !important;
        margin-right: 12px !important;
        padding: 0 16px !important;
        border: 0 !important;
        border-radius: 999px !important;
        background: ${GREY} !important;
        color: ${INK} !important;
        cursor: pointer !important;
        transform: translateX(-8px) !important;
        font-family: "Fields", var(--font-heading-family, serif) !important;
        font-size: 14px !important;
        font-weight: 600 !important;
        line-height: 1 !important;
        letter-spacing: 0 !important;
      }
      .solinia-custom-upsell {
        padding: 24px 24px 28px;
        border-top: 1px solid rgba(107, 79, 154, 0.14);
        border-bottom: 1px solid rgba(107, 79, 154, 0.14);
        background: #fff;
        color: ${INK};
        letter-spacing: 0;
      }
      .solinia-custom-upsell__heading {
        margin: 0 0 18px;
        color: ${INK};
        font-family: "Fields", var(--font-heading-family, serif);
        font-size: 22px;
        font-weight: 600;
        line-height: 1.1;
        text-align: center;
        letter-spacing: 0;
      }
      .solinia-custom-upsell__card {
        display: grid;
        grid-template-columns: 72px minmax(0, 1fr) auto;
        align-items: center;
        gap: 14px;
        padding: 16px;
        border-radius: 8px;
        background: #f6f2fa;
      }
      .solinia-custom-upsell__image {
        width: 64px;
        height: 64px;
        border-radius: 8px;
        object-fit: contain;
        background: #fff;
      }
      .solinia-custom-upsell__title {
        margin: 0 0 5px;
        color: ${INK};
        font-family: "New Atten", var(--font-body-family, sans-serif);
        font-size: 15px;
        font-weight: 700;
        line-height: 1.2;
        letter-spacing: 0;
      }
      .solinia-custom-upsell__price {
        margin: 0;
        color: ${PURPLE_DARK};
        font-family: "New Atten", var(--font-body-family, sans-serif);
        font-size: 15px;
        line-height: 1.1;
        letter-spacing: 0;
      }
      .solinia-custom-upsell__actions {
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }
      .solinia-custom-upsell__add {
        min-height: 36px;
        padding: 0 16px;
        border: 0;
        border-radius: 999px;
        background: ${PURPLE};
        color: #fff;
        cursor: pointer;
        font-family: "Fields", var(--font-heading-family, serif);
        font-size: 14px;
        font-weight: 600;
        line-height: 1;
        letter-spacing: 0;
      }
      @media (max-width: 520px) {
        .solinia-custom-upsell {
          padding: 22px 16px 26px;
        }
        .solinia-custom-upsell__card {
          grid-template-columns: 58px minmax(0, 1fr) auto;
          gap: 10px;
          padding: 12px;
        }
        .solinia-custom-upsell__image {
          width: 52px;
          height: 52px;
        }
        .solinia-custom-upsell__title,
        .solinia-custom-upsell__price {
          font-size: 13px;
        }
        .solinia-custom-upsell__actions {
          gap: 6px;
        }
        .solinia-custom-upsell .solinia-recharge-info-button {
          min-height: 34px !important;
          padding: 0 12px !important;
          margin-right: 0 !important;
          transform: none !important;
        }
        .solinia-custom-upsell__add {
          min-height: 34px;
          padding: 0 13px;
          font-size: 13px;
        }
      }
      .solinia-recharge-modal {
        position: fixed;
        inset: 0;
        z-index: 2147483000;
        display: none;
        align-items: center;
        justify-content: center;
        padding: 22px;
      }
      .solinia-recharge-modal.is-open { display: flex; }
      .solinia-recharge-modal__overlay {
        position: absolute;
        inset: 0;
        border: 0;
        background: rgba(16, 32, 51, 0.48);
      }
      .solinia-recharge-modal__panel {
        position: relative;
        z-index: 1;
        width: min(720px, calc(100vw - 36px));
        max-height: min(760px, calc(100vh - 36px));
        max-height: min(760px, calc(100dvh - 36px));
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border-radius: 8px;
        background: ${CREAM};
        color: ${INK};
        box-shadow: 0 18px 60px rgba(16, 32, 51, 0.22);
      }
      .solinia-recharge-modal__header {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 22px 24px;
        border-bottom: 1px solid rgba(107, 79, 154, 0.16);
      }
      .solinia-recharge-modal__header h3 {
        margin: 0;
        color: ${INK};
        font-family: "Fields", var(--font-heading-family, serif);
        font-size: 26px;
        font-weight: 600;
        line-height: 1.1;
        letter-spacing: 0;
      }
      .solinia-recharge-modal__close {
        width: 38px;
        height: 38px;
        border: 0;
        background: transparent;
        color: ${PURPLE};
        cursor: pointer;
        font-size: 30px;
        line-height: 1;
      }
      .solinia-recharge-modal__body {
        min-height: 0;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        display: grid;
        grid-template-columns: 220px 1fr;
        gap: 24px;
        align-items: start;
        padding: 24px;
      }
      .solinia-recharge-modal__image {
        width: 100%;
        aspect-ratio: 1;
        border-radius: 7px;
        object-fit: cover;
        background: #f5eefb;
      }
      .solinia-recharge-modal__price {
        margin: 0 0 14px;
        color: ${INK};
        font-family: "Fields", var(--font-heading-family, serif);
        font-size: 24px;
        font-weight: 600;
        line-height: 1.1;
        letter-spacing: 0;
      }
      .solinia-recharge-modal__description {
        color: ${INK};
        font-family: "New Atten", var(--font-body-family, sans-serif);
        font-size: 16px;
        line-height: 1.45;
        letter-spacing: 0;
      }
      .solinia-recharge-modal__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 22px;
      }
      .solinia-recharge-modal__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 44px;
        padding: 0 18px;
        border-radius: 5px;
        font-family: "Fields", var(--font-heading-family, serif);
        font-size: 16px;
        font-weight: 600;
        line-height: 1;
        letter-spacing: 0;
        text-decoration: none;
      }
      .solinia-recharge-modal__button--add {
        border: 0;
        background: ${PURPLE};
        color: #fff;
        cursor: pointer;
      }
      .solinia-recharge-modal__button--link {
        border: 1px solid rgba(107, 79, 154, 0.26);
        background: #f1eef5;
        color: #32195f;
      }
      @media (max-width: 749px) {
        .solinia-recharge-modal {
          align-items: flex-start;
          padding: 14px;
          overflow-y: hidden;
          touch-action: pan-y;
        }
        .solinia-recharge-modal__panel {
          width: calc(100vw - 28px);
          max-height: calc(100vh - 28px);
          max-height: calc(100dvh - 28px);
        }
        .solinia-recharge-modal__body {
          grid-template-columns: 1fr;
          gap: 18px;
          padding: 18px;
        }
        .solinia-recharge-modal__image {
          max-width: 240px;
          margin: 0 auto;
        }
        .solinia-recharge-modal__actions { flex-direction: column; }
      }
    `;
    document.head.appendChild(style);
  }

  function patchRechargeConfig() {
    const cfg = window.RechargeStorefrontConfig?.cart_drawer;
    if (!cfg) return;

    cfg.config ||= {};
    cfg.config.config ||= {};
    cfg.config.config.shipping_progress_bar ||= {};
    cfg.config.config.shipping_progress_bar.enabled = true;
    cfg.config.config.shipping_progress_bar.required_amount = 34.99;
    cfg.config.config.shipping_progress_bar.required_amount_type = 'discounted';

    cfg.config.settings ||= {};
    cfg.config.settings.theme ||= {};
    Object.assign(cfg.config.settings.theme, {
      link_color: PURPLE,
      muted_color: '#4c3b75',
      accent_color: PURPLE,
      primary_color: PURPLE,
      secondary_color: GREY,
      background_color: '#FFFFFF',
      foreground_color: INK,
      primary_foreground_color: '#ffffff',
      secondary_foreground_color: INK,
    });

    const blocks = cfg.config.settings.templates?.line_items?.body?.blocks || [];
    blocks.forEach((block) => {
      if (block.type !== 'cross_sell') return;
      block.settings ||= {};
      block.settings.title_text = '<h4 style="text-align:center;">63% of customers also buy</h4>';
      block.settings.card_background_color = '#F6F2FA';
      block.settings.section_background_color = '#FFFFFF';
      block.settings.add_to_cart_button_text = 'Add';
    });
  }

  async function getCart() {
    const now = Date.now();
    if (lastCart && now - lastCartAt < 5000) return lastCart;
    if (cartPromise) return cartPromise;
    cartPromise = fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Cart request failed: ${r.status}`);
        return r.json();
      })
      .then((cart) => {
        lastCart = cart;
        lastCartAt = Date.now();
        return cart;
      })
      .catch(() => lastCart || { total_price: lastProgressTotal || 0, item_count: 0, items: [] })
      .finally(() => {
        cartPromise = null;
      });
    return cartPromise;
  }

  async function normalizeLegacyLpMagnesiumLines(cart) {
    const legacyLines = (cart?.items || []).filter(isLegacyLpLine);
    if (!legacyLines.length) return cart;
    if (legacyLpNormalizePromise) return legacyLpNormalizePromise;

    legacyLpNormalizePromise = (async () => {
      const quantity = legacyLines.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      if (!quantity) return cart;

      const updates = {};
      legacyLines.forEach((item) => {
        if (item.key) updates[item.key] = 0;
      });

      if (Object.keys(updates).length) {
        const removeResponse = await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
        if (!removeResponse.ok) throw new Error(`Legacy LP line removal failed: ${removeResponse.status}`);
      }

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: MAGNESIUM_BASE_VARIANT_ID,
          quantity,
          properties: {
            '_Landing page offer': `${quantity} Jar${quantity === 1 ? '' : 's'}`,
            '_Solinia real SKU': MAGNESIUM_BASE_SKU,
          },
        }),
      });
      if (!addResponse.ok) throw new Error(`Base magnesium re-add failed: ${addResponse.status}`);

      lastCart = null;
      lastCartAt = 0;
      const normalized = await getCart();
      return normalized;
    })()
      .catch((error) => {
        console.warn('[Solinia] Could not normalize legacy LP cart line', error);
        return cart;
      })
      .finally(() => {
        legacyLpNormalizePromise = null;
      });

    return legacyLpNormalizePromise;
  }

  async function removeIneligibleFreeShippingCredits(cart) {
    const credits = (cart?.items || []).filter(isFreeShippingCreditItem);
    if (!credits.length || getShippingEligibleCartCents(cart) >= THRESHOLD_CENTS) return cart;
    if (freeShippingCleanupPromise) return freeShippingCleanupPromise;

    freeShippingCleanupPromise = (async () => {
      const updates = {};
      credits.forEach((item) => {
        updates[item.key || item.id] = 0;
      });
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error(`Free shipping cleanup failed: ${response.status}`);
      lastCart = null;
      lastCartAt = 0;
      return getCart();
    })()
      .catch((error) => {
        console.warn('[Solinia] Could not remove ineligible free shipping line', error);
        return cart;
      })
      .finally(() => {
        freeShippingCleanupPromise = null;
      });

    return freeShippingCleanupPromise;
  }

  function getDrawerCandidates() {
    const selectors = [
      '[role="dialog"]',
      'cart-drawer',
      'aside',
      '[aria-modal="true"]',
      '[id*="cart" i]',
      '[class*="cart" i]',
      '[id*="recharge" i]',
      '[class*="recharge" i]',
      'body',
    ].join(',');

    return [...new Set([document.body, ...getOpenShadowRoots(), ...queryAllDeep(selectors)])]
      .filter((root) => !root.classList?.contains('solinia-recharge-modal'));
  }

  function getCartRoots() {
    return getDrawerCandidates()
      .filter(rootHasVisibleCart)
      .sort((a, b) => text(a).length - text(b).length)
      .slice(0, 6);
  }

  function rootHasVisibleCart(root) {
    const hasHeading = queryAllDeep('h1,h2,h3,h4,p,span,div', root)
      .some((el) => /^Your cart\b/i.test(text(el)) && isOnscreen(el));
    if (!hasHeading) return false;
    return queryAllDeep('button,a,p,span,div', root)
      .some((el) => /^(Checkout|Check out)$/i.test(text(el)) && isOnscreen(el));
  }

  function rootLooksLikeCart(root) {
    if (!root) return false;
    if (root.matches?.('cart-drawer,#recharge-storefront-experiences__cart-drawer')) return true;
    return queryAllDeep('h1,h2,h3,h4,p,span,div', root)
      .some((el) => /^Your cart\b/i.test(text(el)) && isOnscreen(el));
  }

  function getRechargeCartRoot() {
    return document.querySelector('#recharge-storefront-experiences__cart-drawer')?.shadowRoot || null;
  }

  function setInactiveRechargeHostPassthrough() {
    const host = document.querySelector('#recharge-storefront-experiences__cart-drawer');
    if (!host) return;
    const root = host.shadowRoot;
    const rechargeCartIsOpen = !!root && rootHasVisibleCart(root);
    if (rechargeCartIsOpen) {
      host.removeAttribute('data-solinia-recharge-passthrough');
      host.style.removeProperty('pointer-events');
      host.style.removeProperty('z-index');
      return;
    }
    host.setAttribute('data-solinia-recharge-passthrough', '');
    host.style.setProperty('pointer-events', 'none', 'important');
    host.style.setProperty('z-index', '0', 'important');
  }

  function styleInfoButton(button) {
    button.style.setProperty('display', 'inline-flex', 'important');
    button.style.setProperty('align-items', 'center', 'important');
    button.style.setProperty('justify-content', 'center', 'important');
    button.style.setProperty('min-height', '36px', 'important');
    button.style.setProperty('margin-right', '12px', 'important');
    button.style.setProperty('padding', '0 16px', 'important');
    button.style.setProperty('border', '0', 'important');
    button.style.setProperty('border-radius', '999px', 'important');
    button.style.setProperty('background', GREY, 'important');
    button.style.setProperty('color', INK, 'important');
    button.style.setProperty('cursor', 'pointer', 'important');
    button.style.setProperty('transform', 'translateX(-8px)', 'important');
    button.style.setProperty('font-family', '"Fields", var(--font-heading-family, serif)', 'important');
    button.style.setProperty('font-size', '14px', 'important');
    button.style.setProperty('font-weight', '600', 'important');
    button.style.setProperty('line-height', '1', 'important');
    button.style.setProperty('letter-spacing', '0', 'important');
  }

  function styleProgress(progressEl) {
    progressEl.style.setProperty('display', 'block', 'important');
    progressEl.style.setProperty('width', '100%', 'important');
    progressEl.style.setProperty('box-sizing', 'border-box', 'important');
    progressEl.style.setProperty('flex', '0 0 100%', 'important');
    progressEl.style.setProperty('padding', '20px 24px 22px', 'important');
    progressEl.style.setProperty('border-bottom', '1px solid rgba(107, 79, 154, 0.16)', 'important');
    progressEl.style.setProperty('background', CREAM, 'important');
    const label = progressEl.querySelector('.solinia-recharge-progress__text');
    if (label) {
      label.style.setProperty('margin', '0 0 14px', 'important');
      label.style.setProperty('color', '#4c3b75', 'important');
      label.style.setProperty('font-family', '"New Atten", var(--font-body-family, sans-serif)', 'important');
      label.style.setProperty('font-size', '15px', 'important');
      label.style.setProperty('line-height', '1.3', 'important');
      label.style.setProperty('text-align', 'center', 'important');
      label.style.setProperty('letter-spacing', '0', 'important');
    }
    const track = progressEl.querySelector('.solinia-recharge-progress__track');
    if (track) {
      track.style.setProperty('height', '10px', 'important');
      track.style.setProperty('overflow', 'hidden', 'important');
      track.style.setProperty('border-radius', '999px', 'important');
      track.style.setProperty('background', '#ece7ef', 'important');
    }
    const fill = progressEl.querySelector('.solinia-recharge-progress__track span');
    if (fill) {
      fill.style.setProperty('display', 'block', 'important');
      fill.style.setProperty('height', '100%', 'important');
      fill.style.setProperty('min-width', '10px', 'important');
      fill.style.setProperty('border-radius', 'inherit', 'important');
      fill.style.setProperty('background', PURPLE, 'important');
      fill.style.setProperty('transition', 'width 180ms ease', 'important');
    }
  }

  function parseMoneyToCents(value) {
    const match = String(value || '').match(/\$([\d,]+(?:\.\d{2})?)/);
    if (!match) return 0;
    return Math.round(Number(match[1].replace(/,/g, '')) * 100);
  }

  function getDisplayedCartTotal(root) {
    const rootText = text(root);
    const subtotal = rootText.match(/Subtotal[\s\S]{0,180}/i)?.[0] || '';
    const prices = [...subtotal.matchAll(/\$[\d,]+(?:\.\d{2})?/g)].map((match) => parseMoneyToCents(match[0]));
    return prices.filter(Boolean).pop() || 0;
  }

  function renderProgress(progressEl, totalCents) {
    lastProgressTotal = Math.max(0, totalCents || 0);
    const remaining = Math.max(0, THRESHOLD_CENTS - lastProgressTotal);
    const progress = Math.min(100, Math.round((lastProgressTotal / THRESHOLD_CENTS) * 100));
    progressEl.innerHTML = `
      <p class="solinia-recharge-progress__text">${
        remaining > 0 ? `Spend ${money(remaining)} more for free shipping` : 'You unlocked free shipping'
      }</p>
      <div class="solinia-recharge-progress__track" aria-hidden="true"><span style="width: ${progress}%;"></span></div>
    `;
    styleProgress(progressEl);
  }

  function getUpsellRoots() {
    return getDrawerCandidates()
      .filter((root) => {
        const hasUpsell = queryAllDeep('a,p,span,h1,h2,h3,h4,strong,div', root)
          .some((el) => /Deep Sleep Shower Steamers/i.test(text(el)) && isOnscreen(el));
        const hasAdd = queryAllDeep('button,a', root)
          .some((el) => /^Add$/i.test(text(el)) && isOnscreen(el));
        return hasUpsell && hasAdd;
      })
      .sort((a, b) => text(a).length - text(b).length)
      .slice(0, 8);
  }

  function findCartHeaderContainer(root) {
    const heading = queryAllDeep('h1,h2,h3,h4,p,span,div', root)
      .filter((el) => /^Your cart\b/i.test(text(el)) && isOnscreen(el))
      .sort((a, b) => text(a).length - text(b).length)[0];
    if (!heading) return null;
    const closestHeader = heading.closest?.('.drawer__header,[class*="cart-header" i],[class*="header" i]');
    if (closestHeader && closestHeader !== heading && root.contains?.(closestHeader)) return closestHeader;
    let node = heading;
    for (let i = 0; i < 8 && node && node !== root; i += 1) {
      const nodeText = text(node);
      const hasCloseControl = queryAllDeep('button,a,[role="button"]', node)
        .some((el) => /close|dismiss|^[×x]$/i.test([
          el.getAttribute?.('aria-label'),
          el.getAttribute?.('title'),
          text(el),
        ].filter(Boolean).join(' ')));
      if (node !== heading && /^Your cart\b/i.test(nodeText) && hasCloseControl) return node;
      node = node.parentElement;
    }
    return heading.parentElement || heading;
  }

  function ensureProgress(root, cart) {
    if (!root) return;
    queryAllDeep('#SoliniaCartProgress, .solinia-recharge-progress', root).forEach((el) => el.remove());
    if (!cart?.items?.length) return;
    const header = findCartHeaderContainer(root);
    const headerRow = header?.closest?.('.drawer__header,[class*="cart-header" i],[class*="header" i]') || header;
    const progress = document.createElement('div');
    progress.id = 'SoliniaCartProgress';
    progress.className = 'solinia-recharge-progress';
    renderProgress(progress, getShippingEligibleCartCents(cart) || cart.total_price || getDisplayedCartTotal(root));
    if (headerRow?.parentNode) {
      headerRow.parentNode.insertBefore(progress, headerRow.nextSibling);
    } else if (header?.parentNode) {
      header.parentNode.insertBefore(progress, header.nextSibling);
    } else if (root.appendChild) {
      root.appendChild(progress);
    }
  }

  function getLpGiftSummary(cart, root) {
    const rootText = text(root);
    let hasEligibleLpOffer = /LP [23] Jar Offer/i.test(rootText);
    let hasGuide = /The Ultimate Sleep Hacks Guide/i.test(rootText);
    let hasSteamers7 = /Deep Sleep Shower Steamers\s*-\s*7 Pack/i.test(rootText);
    let hasShippingCredit = false;
    let originalCents = 0;
    const shippingEligibleCents = getShippingEligibleCartCents(cart);

    if (cart?.items?.length) {
      originalCents = cart.items.reduce((sum, item) => {
        const sku = item.sku || '';
        const variantTitle = item.variant_title || '';
        const productTitle = item.product_title || item.title || '';
        const isLpMagnesium = /^ML-4-LAV-LP-/.test(sku) || /^LP \d+ Jar Offer$/i.test(variantTitle);
        const isGuide = /The Ultimate Sleep Hacks Guide/i.test(productTitle);
        const isSteamers7 = /Deep Sleep Shower Steamers\s*-\s*7 Pack/i.test(productTitle);
        const isShippingCredit = isFreeShippingCreditItem(item);

        if (isLpMagnesium) {
          hasEligibleLpOffer = hasEligibleLpOffer || item.quantity >= 2 || /LP [23] Jar Offer/i.test(variantTitle);
          return sum + (2999 * item.quantity);
        }
        if (isGuide) {
          hasGuide = true;
          hasEligibleLpOffer = true;
          return sum + (LP_GIFT_RETAIL_CENTS.guide * item.quantity);
        }
        if (isSteamers7) {
          hasSteamers7 = true;
          hasEligibleLpOffer = true;
          return sum + (LP_GIFT_RETAIL_CENTS.steamers7 * item.quantity);
        }
        if (isShippingCredit) {
          hasShippingCredit = true;
          hasEligibleLpOffer = true;
          return sum + (LP_GIFT_RETAIL_CENTS.shipping * item.quantity);
        }
        return sum + (item.original_line_price ?? item.line_price ?? item.final_line_price ?? 0);
      }, 0);
    }

    const shouldShowShippingGift =
      shippingEligibleCents >= THRESHOLD_CENTS &&
      (hasEligibleLpOffer || hasGuide || hasSteamers7 || hasShippingCredit);
    if (cart?.items?.length && shouldShowShippingGift && !hasShippingCredit) originalCents += LP_GIFT_RETAIL_CENTS.shipping;
    if (!cart?.items?.length && shouldShowShippingGift) {
      originalCents = LP_GIFT_RETAIL_CENTS.shipping;
      if (hasGuide || hasEligibleLpOffer) originalCents += LP_GIFT_RETAIL_CENTS.guide;
      if (hasSteamers7 || hasEligibleLpOffer) originalCents += LP_GIFT_RETAIL_CENTS.steamers7;
    }

    return { shouldShowShippingGift, originalCents, hasGuide, hasSteamers7, hasShippingCredit, shippingEligibleCents };
  }

  function hideRealFreeShippingCreditLine(root) {
    const realLine = findCartLineContainer(root, /\bFree Shipping\b/i, { requireMoney: true });
    if (realLine && !realLine.classList?.contains('solinia-lp-free-shipping-line')) {
      realLine.style.setProperty('display', 'none', 'important');
    }
  }

  function hideLpVariantLabels(root) {
    queryAllDeep('p,span,div,small', root).forEach((el) => {
      if (el.children.length) return;
      const value = text(el);
      if (/^LP \d+ Jar Offer$/i.test(value)) {
        el.textContent = '';
        el.style.setProperty('display', 'none', 'important');
      }
    });
  }

  function ensureFreeShippingGiftLine(root, cart) {
    const summary = getLpGiftSummary(cart, root);
    const existing = queryAllDeep('.solinia-lp-free-shipping-line', root);
    if (!summary.shouldShowShippingGift) {
      existing.forEach((line) => line.remove());
      return;
    }

    const line = existing[0] || document.createElement('div');
    existing.slice(1).forEach((extra) => extra.remove());
    line.className = 'solinia-lp-free-shipping-line';
    line.setAttribute('data-solinia-lp-free-shipping-line', '');
    line.innerHTML = `
      <span class="solinia-lp-free-shipping-line__image" aria-hidden="true">FREE</span>
      <p class="solinia-lp-free-shipping-line__title">Free Shipping</p>
      <span class="solinia-lp-free-shipping-line__price"><s>$6.99</s><strong>$0</strong></span>
    `;
    line.style.setProperty('display', 'grid', 'important');
    line.style.setProperty('grid-template-columns', '76px 1fr auto', 'important');
    line.style.setProperty('align-items', 'center', 'important');
    line.style.setProperty('gap', '16px', 'important');
    line.style.setProperty('padding', '18px 24px', 'important');
    line.style.setProperty('border-bottom', '1px solid rgba(107, 79, 154, 0.14)', 'important');
    line.style.setProperty('background', '#fff', 'important');
    line.style.setProperty('color', INK, 'important');
    line.style.setProperty('font-family', '"New Atten", var(--font-body-family, sans-serif)', 'important');
    line.style.setProperty('letter-spacing', '0', 'important');
    const icon = line.querySelector('.solinia-lp-free-shipping-line__image');
    if (icon) {
      icon.style.setProperty('display', 'inline-flex', 'important');
      icon.style.setProperty('align-items', 'center', 'important');
      icon.style.setProperty('justify-content', 'center', 'important');
      icon.style.setProperty('width', '66px', 'important');
      icon.style.setProperty('height', '66px', 'important');
      icon.style.setProperty('border-radius', '7px', 'important');
      icon.style.setProperty('background', '#f6f2fa', 'important');
      icon.style.setProperty('color', PURPLE, 'important');
      icon.style.setProperty('font-size', '13px', 'important');
      icon.style.setProperty('font-family', '"Fields", var(--font-heading-family, serif)', 'important');
    }
    const title = line.querySelector('.solinia-lp-free-shipping-line__title');
    if (title) {
      title.style.setProperty('margin', '0', 'important');
      title.style.setProperty('color', INK, 'important');
      title.style.setProperty('font-family', '"Fields", var(--font-heading-family, serif)', 'important');
      title.style.setProperty('font-size', '17px', 'important');
      title.style.setProperty('font-weight', '600', 'important');
      title.style.setProperty('line-height', '1.1', 'important');
    }
    const price = line.querySelector('.solinia-lp-free-shipping-line__price');
    if (price) {
      price.style.setProperty('display', 'inline-flex', 'important');
      price.style.setProperty('gap', '8px', 'important');
      price.style.setProperty('align-items', 'baseline', 'important');
      price.style.setProperty('color', PURPLE, 'important');
      price.style.setProperty('font-family', '"New Atten", var(--font-body-family, sans-serif)', 'important');
      price.style.setProperty('font-size', '16px', 'important');
      price.style.setProperty('white-space', 'nowrap', 'important');
    }
    price?.querySelector('s')?.style.setProperty('color', INK, 'important');
    price?.querySelector('s')?.style.setProperty('opacity', '0.75', 'important');
    price?.querySelector('strong')?.style.setProperty('color', PURPLE, 'important');

    const productContainer = findCartLineContainer(root, /Magnesium Sleep Cream/i);
    if (productContainer?.after) productContainer.after(line);
    else if (!existing[0]) root.append(line);
  }

  function isCartLineCandidate(el, root, pattern, options = {}) {
    if (!el || el === root || el === document.body || el === document.documentElement) return false;
    if (el.classList?.contains('solinia-lp-free-shipping-line')) return pattern.test(text(el));
    if (!isVisible(el)) return false;
    const value = text(el);
    if (!pattern.test(value)) return false;
    if (/Subtotal|Checkout|customers also buy|Your cart/i.test(value)) return false;
    if (options.requireMoney && !/\$[\d,]+(?:\.\d{2})?|Saving\s+\$|FREE/i.test(value)) return false;
    const rect = el.getBoundingClientRect();
    if (rect.width < 260 || rect.height < 60) return false;
    if (value.length > 520) return false;
    return true;
  }

  function findCartLineContainer(root, pattern, options = {}) {
    const candidates = queryAllDeep('li, article, section, div', root)
      .filter((el) => isCartLineCandidate(el, root, pattern, options))
      .sort((a, b) => {
        const classA = String(a.className || '');
        const classB = String(b.className || '');
        const aOuterRow = /border-b/i.test(classA) && /(py-|px-drawer|gap-)/i.test(classA) ? 0 : 1;
        const bOuterRow = /border-b/i.test(classB) && /(py-|px-drawer|gap-)/i.test(classB) ? 0 : 1;
        if (aOuterRow !== bOuterRow) return aOuterRow - bOuterRow;
        const aLine = /border-b|line|item/i.test(classA) ? 0 : 1;
        const bLine = /border-b|line|item/i.test(classB) ? 0 : 1;
        if (aLine !== bLine) return aLine - bLine;
        return text(a).length - text(b).length;
      });
    return candidates[0] || null;
  }

  function reorderLpCartLines(root) {
    const magnesium = findCartLineContainer(root, /Magnesium Sleep Cream/i);
    if (!magnesium || !magnesium.parentNode) return;

    const parent = magnesium.parentNode;
    const freeShipping = queryAllDeep('.solinia-lp-free-shipping-line', root).find((el) => el.isConnected);
    const guide = findCartLineContainer(root, /The Ultimate Sleep Hacks Guide/i);
    const steamers = findCartLineContainer(root, /Deep Sleep Shower Steamers\s*-\s*7 Pack/i);
    const realShipping = findCartLineContainer(root, /\bFree Shipping\b/i, { requireMoney: true });

    let anchor = magnesium;
    const firstGiftBeforeMagnesium = [realShipping, guide, steamers]
      .filter((line) => line && line.parentNode === parent)
      .find((line) => {
        try {
          return !!(line.compareDocumentPosition(magnesium) & Node.DOCUMENT_POSITION_FOLLOWING);
        } catch {
          return false;
        }
      });
    if (firstGiftBeforeMagnesium) {
      parent.insertBefore(magnesium, firstGiftBeforeMagnesium);
    }
    [freeShipping, guide, steamers].forEach((line) => {
      if (!line || line === anchor || line.parentNode !== parent) return;
      anchor.after(line);
      anchor = line;
    });
    if (realShipping && realShipping !== freeShipping) realShipping.style.setProperty('display', 'none', 'important');
  }

  function smallestLineContainer(titleEl, root) {
    let node = titleEl;
    for (let i = 0; i < 12 && node && node !== root.parentElement; i += 1) {
      const nodeText = text(node);
      const hasMoney = /\$[\d,]+(?:\.\d{2})?|\bfree\b/i.test(nodeText);
      const looksLikeFooter = /Subtotal|Checkout|customers also buy/i.test(nodeText);
      if (hasMoney && !looksLikeFooter) return node;
      node = node.parentElement;
    }
    return titleEl.parentElement;
  }

  function setGiftPriceMarkup(container, retail) {
    if (!container) return;
    const leaves = queryAllDeep('span,p,strong,div', container)
      .filter((el) => !el.children.length && isVisible(el) && (/\$[\d,]+(?:\.\d{2})?/i.test(text(el)) || /^free$/i.test(text(el))));
    const priceLeaf = leaves.sort((a, b) => text(a).length - text(b).length)[0];
    if (!priceLeaf || priceLeaf.classList.contains('solinia-lp-gift-price')) return;
    priceLeaf.className = `${priceLeaf.className || ''} solinia-lp-gift-price`.trim();
    priceLeaf.innerHTML = `<s>${retail}</s><strong>$0</strong>`;
    priceLeaf.style.setProperty('display', 'inline-flex', 'important');
    priceLeaf.style.setProperty('align-items', 'baseline', 'important');
    priceLeaf.style.setProperty('gap', '8px', 'important');
    priceLeaf.style.setProperty('color', PURPLE, 'important');
    priceLeaf.style.setProperty('font-family', '"New Atten", var(--font-body-family, sans-serif)', 'important');
    priceLeaf.style.setProperty('letter-spacing', '0', 'important');
    priceLeaf.style.setProperty('white-space', 'nowrap', 'important');
    priceLeaf.querySelector('s')?.style.setProperty('color', INK, 'important');
    priceLeaf.querySelector('s')?.style.setProperty('opacity', '0.75', 'important');
    priceLeaf.querySelector('strong')?.style.setProperty('color', PURPLE, 'important');
    priceLeaf.querySelector('strong')?.style.setProperty('font-weight', '700', 'important');
  }

  function normalizeGiftLinePrices(root) {
    [
      { pattern: /The Ultimate Sleep Hacks Guide/i, retail: '$24.99' },
      { pattern: /Deep Sleep Shower Steamers\s*-\s*7 Pack/i, retail: '$14.99' },
    ].forEach((gift) => {
      const title = queryAllDeep('a,p,span,h1,h2,h3,h4,strong,div', root)
        .filter((el) => !el.children.length && !/customers also buy/i.test(text(el)))
        .find((el) => gift.pattern.test(text(el)) && isVisible(el));
      if (!title) return;
      setGiftPriceMarkup(smallestLineContainer(title, root), gift.retail);
    });
  }

  function cartHasGiftItem(cart, pattern) {
    return (cart?.items || []).some((item) => {
      const title = item?.product_title || item?.title || '';
      return pattern.test(title) && Boolean(getLineProperty(item, '_Solinia LP gift'));
    });
  }

  function setGiftLineThumbnail(container, src, alt) {
    if (!container || !src) return;
    const image = queryAllDeep('img', container).find((img) => isVisible(img)) ||
      queryAllDeep('img', container)[0];
    if (!image) return;
    image.src = src;
    image.srcset = '';
    image.sizes = '';
    image.alt = alt;
    image.style.setProperty('object-fit', 'contain', 'important');
    image.style.setProperty('object-position', 'center', 'important');
    image.style.setProperty('background', '#f6f2fa', 'important');
    image.closest('picture')?.querySelectorAll('source').forEach((source) => {
      source.srcset = '';
      source.removeAttribute('srcset');
    });
  }

  function normalizeGiftLineThumbnails(root, cart) {
    [
      {
        pattern: /The Ultimate Sleep Hacks Guide/i,
        src: CART_GIFT_THUMBNAILS.guide,
        alt: 'Free Ultimate Sleep Hacks Guide',
      },
      {
        pattern: /Deep Sleep Shower Steamers\s*-\s*7 Pack/i,
        src: CART_GIFT_THUMBNAILS.steamers7,
        alt: 'Free Deep Sleep Shower Steamers - 7 Pack',
      },
    ].forEach((gift) => {
      if (!cartHasGiftItem(cart, gift.pattern)) return;
      setGiftLineThumbnail(findCartLineContainer(root, gift.pattern), gift.src, gift.alt);
    });
  }

  function findSubtotalContainer(root) {
    const label = queryAllDeep('p,span,strong,div,h1,h2,h3,h4', root)
      .filter((el) => /^Subtotal\b/i.test(text(el)) && isVisible(el))
      .sort((a, b) => text(a).length - text(b).length)[0];
    let node = label;
    for (let i = 0; i < 10 && node && node !== root.parentElement; i += 1) {
      if (/Subtotal/i.test(text(node)) && /Checkout|Check out/i.test(text(node))) return node;
      node = node.parentElement;
    }
    return label?.parentElement || null;
  }

  function adjustLpSubtotal(root, cart) {
    if (!cart?.items?.length) return;
    const summary = getLpGiftSummary(cart, root);
    const container = findSubtotalContainer(root);
    if (!container) return;
    if (!summary.shouldShowShippingGift || summary.originalCents <= cart.total_price) {
      queryAllDeep('span,strong,p,div', container)
        .filter((el) => !el.children.length && isVisible(el))
        .forEach((leaf) => {
          const cents = parseMoneyToCents(text(leaf));
          if (!cents || cents === cart.total_price) return;
          if (leaf.classList.contains('solinia-lp-subtotal-original') || text(leaf) === '$101.95') leaf.remove();
        });
      return;
    }
    const current = money(cart.total_price);
    const original = money(summary.originalCents);
    const priceLeaves = queryAllDeep('span,strong,p,div', container)
      .filter((el) => !el.children.length && /\$[\d,]+(?:\.\d{2})?/.test(text(el)) && isVisible(el));
    const currentLeaf = [...priceLeaves].reverse().find((el) => parseMoneyToCents(text(el)) === cart.total_price) || priceLeaves[priceLeaves.length - 1];
    if (!currentLeaf) return;
    currentLeaf.textContent = current;
    currentLeaf.style.setProperty('color', PURPLE, 'important');
    currentLeaf.style.setProperty('font-weight', '700', 'important');

    let originalLeaf = priceLeaves.find((el) => el !== currentLeaf && parseMoneyToCents(text(el)) >= cart.total_price);
    if (!originalLeaf) {
      originalLeaf = document.createElement('span');
      currentLeaf.before(originalLeaf);
    }
    priceLeaves.forEach((leaf) => {
      if (leaf === currentLeaf || parseMoneyToCents(text(leaf)) === cart.total_price) return;
      leaf.classList.add('solinia-lp-subtotal-original');
      leaf.textContent = original;
      leaf.style.setProperty('margin-right', '10px', 'important');
      leaf.style.setProperty('color', INK, 'important');
      leaf.style.setProperty('opacity', '0.95', 'important');
      leaf.style.setProperty('font-weight', '700', 'important');
      leaf.style.setProperty('text-decoration', 'line-through', 'important');
    });
    originalLeaf.classList.add('solinia-lp-subtotal-original');
    originalLeaf.textContent = original;
    originalLeaf.style.setProperty('margin-right', '10px', 'important');
    originalLeaf.style.setProperty('color', INK, 'important');
    originalLeaf.style.setProperty('opacity', '0.95', 'important');
    originalLeaf.style.setProperty('font-weight', '700', 'important');
    originalLeaf.style.setProperty('text-decoration', 'line-through', 'important');
  }

  function normalizeLpCart(root, cart) {
    hideLpVariantLabels(root);
    hideRealFreeShippingCreditLine(root);
    ensureFreeShippingGiftLine(root, cart);
    normalizeGiftLineThumbnails(root, cart);
    normalizeGiftLinePrices(root);
    reorderLpCartLines(root);
    adjustLpSubtotal(root, cart);
  }

  function recolorCartButtons(root) {
    queryAllDeep('button, a', root).forEach((button) => {
      const label = text(button);
      if (!/^(Add|Checkout|Check out|Add to cart)$/i.test(label)) return;
      if (button.classList.contains('solinia-recharge-info-button')) return;
      button.style.setProperty('background-color', PURPLE, 'important');
      button.style.setProperty('background', PURPLE, 'important');
      button.style.setProperty('color', '#fff', 'important');
      button.style.setProperty('border-color', PURPLE, 'important');
      button.style.setProperty('border-radius', '999px', 'important');
      button.style.setProperty('font-family', '"Fields", var(--font-heading-family, serif)', 'important');
      button.style.setProperty('letter-spacing', '0', 'important');
    });
  }

  function updateCrossSellHeading(root) {
    queryAllDeep('h1,h2,h3,h4,p,span,div', root).forEach((el) => {
      if (el.children.length) return;
      if (/^(Goes great with|\d+% of customers also buy)$/i.test(text(el))) {
        el.textContent = '63% of customers also buy';
      }
    });
  }

  function findUpsellCard(root) {
    const title = queryAllDeep('a,p,span,h1,h2,h3,h4,strong', root)
      .find((el) => /Deep Sleep Shower Steamers/i.test(text(el)) && isVisible(el));
    if (!title) return null;

    let node = title;
    for (let i = 0; i < 10 && node && node !== root.parentElement; i += 1) {
      const nodeText = text(node);
      if (
        /Deep Sleep Shower Steamers/i.test(nodeText) &&
        queryAllDeep('button, a', node).some((button) => /^Add$/i.test(text(button)) && isVisible(button))
      ) {
        return node;
      }
      node = node.parentElement;
    }
    return title.parentElement;
  }

  function findUpsellAddButton(root) {
    const exactCard = findUpsellCard(root);
    if (exactCard) {
      const addButton = queryAllDeep('button, a', exactCard)
        .find((button) => /^Add$/i.test(text(button)) && isVisible(button));
      if (addButton) return { card: exactCard, addButton };
    }

    for (const addButton of queryAllDeep('button, a', root).filter((button) => /^Add$/i.test(text(button)) && isVisible(button))) {
      let node = addButton.parentElement;
      for (let i = 0; i < 12 && node && node !== root.parentElement; i += 1) {
        if (/Deep Sleep Shower Steamers/i.test(text(node))) return { card: node, addButton };
        node = node.parentElement;
      }
    }

    const title = queryAllDeep('a,p,span,h1,h2,h3,h4,strong', root)
      .find((el) => /Deep Sleep Shower Steamers/i.test(text(el)) && isVisible(el));
    const addButton = queryAllDeep('button, a', root).find((button) => /^Add$/i.test(text(button)) && isVisible(button));
    return title && addButton ? { card: title.parentElement, addButton } : { card: null, addButton: null };
  }

  function placeInfoButton(card, addButton, root) {
    const existingButtons = [
      ...queryAllDeep('.solinia-recharge-info-button'),
      ...queryAllDeep('[data-solinia-recharge-info]')
    ];
    existingButtons
      .filter((button) => !isVisible(button) || !button.parentElement?.contains(addButton))
      .forEach((button) => button.remove());

    const infoAlready = existingButtons.find((button) => {
      if (!button.isConnected) return false;
      if (button.nextElementSibling === addButton || button.parentElement === addButton.parentElement) return true;
      let node = button.parentElement;
      for (let i = 0; i < 12 && node; i += 1) {
        if (/Deep Sleep Shower Steamers/i.test(text(node)) || node === card) return true;
        node = node.parentElement;
      }
      return false;
    });

    if (infoAlready) {
      if (infoAlready.nextElementSibling !== addButton) addButton.before(infoAlready);
      infoAlready.style.removeProperty('display');
      styleInfoButton(infoAlready);
      return;
    }

    const info = document.createElement('button');
    info.type = 'button';
    info.className = 'solinia-recharge-info-button';
    info.setAttribute('data-solinia-recharge-info', '');
    info.textContent = 'Info';
    styleInfoButton(info);
    addButton.before(info);

    const actionParent = addButton.parentElement;
    if (actionParent) {
      actionParent.style.setProperty('display', 'flex', 'important');
      actionParent.style.setProperty('align-items', 'center', 'important');
      actionParent.style.setProperty('justify-content', 'flex-end', 'important');
      actionParent.style.setProperty('gap', '8px', 'important');
      actionParent.style.setProperty('flex-wrap', 'nowrap', 'important');
    }
  }

  function ensureInfoButton(root) {
    const { card, addButton } = findUpsellAddButton(root);
    if (!addButton) return;
    placeInfoButton(card, addButton, root);
  }

  function ensureGlobalInfoButton() {
    const { card, addButton } = findUpsellAddButton(document.body);
    if (!addButton) return;
    placeInfoButton(card, addButton, document.body);
  }

  function findCartFooterContainer(root) {
    const checkout = queryAllDeep('button,a', root)
      .filter((button) => /^(Checkout|Check out)$/i.test(text(button)) && isVisible(button))
      .sort((a, b) => text(a).length - text(b).length)[0];
    if (!checkout) return null;
    let node = checkout;
    let best = checkout.parentElement;
    for (let i = 0; i < 10 && node && node !== root; i += 1) {
      const nodeText = text(node);
      if (/Subtotal/i.test(nodeText) && /Checkout|Check out/i.test(nodeText)) best = node;
      node = node.parentElement;
    }
    return best;
  }

  function rootHasVisibleSteamersUpsell(root) {
    const title = queryAllDeep('a,p,span,h1,h2,h3,h4,strong', root)
      .find((el) => !el.closest?.('[data-solinia-custom-upsell]') && /Deep Sleep Shower Steamers/i.test(text(el)) && isVisible(el));
    if (!title) return false;

    let node = title;
    for (let i = 0; i < 10 && node && node !== root.parentElement; i += 1) {
      if (node.closest?.('[data-solinia-custom-upsell]')) return false;
      const nodeText = text(node);
      const hasTitle = /Deep Sleep Shower Steamers/i.test(nodeText);
      const hasAdd = queryAllDeep('button,a', node)
        .some((el) => !el.closest?.('[data-solinia-custom-upsell]') && /^Add$/i.test(text(el)) && isVisible(el));
      if (hasTitle && hasAdd) return true;
      node = node.parentElement;
    }

    return false;
  }

  function ensureCustomUpsell(root, cart) {
    if (!root) return;
    const existing = queryAllDeep('[data-solinia-custom-upsell]', root);
    if (!cart?.items?.length || cartHasShowerSteamers(cart)) {
      existing.forEach((el) => el.remove());
      return;
    }
    if (rootHasVisibleSteamersUpsell(root)) {
      existing.forEach((el) => el.remove());
      return;
    }

    const upsell = existing[0] || document.createElement('section');
    existing.slice(1).forEach((el) => el.remove());
    upsell.className = 'solinia-custom-upsell';
    upsell.setAttribute('data-solinia-custom-upsell', '');
    upsell.innerHTML = `
      <h3 class="solinia-custom-upsell__heading">63% of customers also buy</h3>
      <div class="solinia-custom-upsell__card">
        <img class="solinia-custom-upsell__image" src="${UPSELL_FALLBACK.featured_image}" alt="Deep Sleep Shower Steamers">
        <div class="solinia-custom-upsell__copy">
          <p class="solinia-custom-upsell__title">Deep Sleep Shower Steamers</p>
          <p class="solinia-custom-upsell__price">$24.99</p>
        </div>
        <div class="solinia-custom-upsell__actions">
          <button class="solinia-recharge-info-button" type="button" data-solinia-recharge-info>Info</button>
          <button class="solinia-custom-upsell__add" type="button" data-solinia-custom-upsell-add data-variant-id="${UPSELL_VARIANT_ID}">Add</button>
        </div>
      </div>
    `;

    const footer = findCartFooterContainer(root);
    if (!existing[0]) {
      if (footer?.parentNode) footer.parentNode.insertBefore(upsell, footer);
      else root.appendChild(upsell);
    }
  }

  function stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return text(div);
  }

  function getUpsellProduct() {
    if (!productPromise) {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 2500);
      productPromise = fetch(`/products/${UPSELL_HANDLE}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      })
        .then((r) => {
          if (!r.ok) throw new Error(`Product request failed: ${r.status}`);
          return r.json();
        })
        .catch(() => UPSELL_FALLBACK)
        .finally(() => window.clearTimeout(timer));
    }
    return productPromise;
  }

  function renderInfoModal(product) {
    let modal = document.getElementById('SoliniaRechargeInfoModal');
    const variant = product.variants?.[0] || UPSELL_FALLBACK.variants[0];
    const image = product.featured_image || product.images?.[0] || '';
    const price = variant ? money(variant.price) : '';
    const description = stripHtml(product.description).split(' ').slice(0, 70).join(' ');

    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'SoliniaRechargeInfoModal';
      modal.className = 'solinia-recharge-modal';
      modal.setAttribute('aria-hidden', 'true');
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      document.body.appendChild(modal);
    }

    modal.innerHTML = `
      <button class="solinia-recharge-modal__overlay" type="button" data-solinia-recharge-close aria-label="Close"></button>
      <div class="solinia-recharge-modal__panel">
        <div class="solinia-recharge-modal__header">
          <h3>${product.title}</h3>
          <button class="solinia-recharge-modal__close" type="button" data-solinia-recharge-close aria-label="Close">×</button>
        </div>
        <div class="solinia-recharge-modal__body">
          ${image ? `<img class="solinia-recharge-modal__image" src="${image}" alt="${product.title}">` : ''}
          <div>
            <p class="solinia-recharge-modal__price">${price}</p>
            <div class="solinia-recharge-modal__description">${description}</div>
            <div class="solinia-recharge-modal__actions">
              <button class="solinia-recharge-modal__button solinia-recharge-modal__button--add" type="button" data-solinia-recharge-modal-add data-variant-id="${variant?.id || ''}">Add to cart</button>
              <a class="solinia-recharge-modal__button solinia-recharge-modal__button--link" href="/products/${UPSELL_HANDLE}">View full details</a>
            </div>
          </div>
        </div>
      </div>
    `;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    modal.querySelector('.solinia-recharge-modal__close')?.focus();
  }

  function openInfoModal() {
    renderInfoModal(UPSELL_FALLBACK);
    getUpsellProduct().then(renderInfoModal).catch(() => {});
  }

  function closeInfoModal() {
    const modal = document.getElementById('SoliniaRechargeInfoModal');
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
  }

  async function addModalProduct(button) {
    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;
    button.disabled = true;
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });
      lastCart = null;
      closeInfoModal();
      queueApply();
    } finally {
      button.disabled = false;
    }
  }

  async function addCustomUpsellProduct(button) {
    const variantId = button.getAttribute('data-variant-id') || UPSELL_VARIANT_ID;
    button.disabled = true;
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });
      lastCart = null;
      lastCartAt = 0;
      queueApply();
    } finally {
      button.disabled = false;
    }
  }

  async function applyOverrides() {
    injectStyles();
    patchRechargeConfig();
    setInactiveRechargeHostPassthrough();
    const roots = getCartRoots();
    const rechargeRoot = getRechargeCartRoot();
    const rechargeCartIsOpen = rechargeRoot && rootHasVisibleCart(rechargeRoot);
    const upsellRoots = getUpsellRoots();
    if (!roots.length && !rechargeCartIsOpen && !upsellRoots.length) return;
    let cart = await getCart().catch(() => lastCart || { total_price: lastProgressTotal || 0, item_count: 0, items: [] });
    cart = await normalizeLegacyLpMagnesiumLines(cart);
    cart = await removeIneligibleFreeShippingCredits(cart);
    ensureProgress(rechargeCartIsOpen ? rechargeRoot : roots[0], cart);
    if (rechargeCartIsOpen && !roots.includes(rechargeRoot)) {
      normalizeLpCart(rechargeRoot, cart);
      ensureCustomUpsell(rechargeRoot, cart);
    }
    roots.forEach((root) => {
      updateCrossSellHeading(root);
      normalizeLpCart(root, cart);
      ensureCustomUpsell(root, cart);
      recolorCartButtons(root);
      ensureInfoButton(root);
    });
    upsellRoots.forEach((root) => {
      updateCrossSellHeading(root);
      recolorCartButtons(root);
      ensureInfoButton(root);
    });
  }

  function queueApply() {
    if (applyQueued) return;
    applyQueued = true;
    window.requestAnimationFrame(() => {
      applyQueued = false;
      applyOverrides().catch(() => {});
    });
  }

  function revealThemeCartDrawer() {
    cleanupForcedCartClose();
    setInactiveRechargeHostPassthrough();
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return false;
    ['display', 'visibility', 'opacity', 'pointer-events', 'transform'].forEach((prop) => {
      drawer.style.removeProperty(prop);
    });
    drawer.style.setProperty('display', 'flex', 'important');
    drawer.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    queueApply();
    return true;
  }

  function openThemeCartDrawer(trigger) {
    const drawer = document.querySelector('cart-drawer');
    if (window.SoliniaCartDrawer?.open) {
      window.SoliniaCartDrawer.open(trigger);
      window.setTimeout(revealThemeCartDrawer, 30);
      return;
    }
    if (drawer?.open) {
      drawer.open(trigger);
      window.setTimeout(revealThemeCartDrawer, 30);
      return;
    }
    if (revealThemeCartDrawer()) return;
    document.getElementById('cart-icon-bubble')?.click();
  }

  function cleanupForcedCartClose() {
    queryAllDeep('[data-solinia-cart-force-closed]').forEach((el) => {
      el.removeAttribute('data-solinia-cart-force-closed');
      ['display', 'visibility', 'opacity', 'pointer-events', 'transform'].forEach((prop) => {
        el.style.removeProperty(prop);
      });
    });
    setInactiveRechargeHostPassthrough();
  }

  function hideElementForCartClose(el) {
    if (!el || el === document.body || el === document.documentElement) return;
    el.setAttribute('data-solinia-cart-force-closed', '');
    if (el.id === 'recharge-storefront-experiences__cart-drawer') {
      el.style.setProperty('pointer-events', 'none', 'important');
      el.style.setProperty('z-index', '0', 'important');
      return;
    }
    el.style.setProperty('display', 'none', 'important');
    el.style.setProperty('visibility', 'hidden', 'important');
    el.style.setProperty('opacity', '0', 'important');
    el.style.setProperty('pointer-events', 'none', 'important');
    el.style.setProperty('transform', 'translateX(100%)', 'important');
  }

  function forceCloseCartDrawer() {
    try {
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true }));
      window.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true }));
    } catch (error) {}

    const drawer = document.querySelector('cart-drawer');
    try { window.SoliniaCartDrawer?.close?.(); } catch (error) {}
    try { drawer?.close?.(); } catch (error) {}

    if (drawer) {
      drawer.classList.remove('active', 'animate');
      drawer.removeAttribute('open');
      ['display', 'visibility', 'opacity', 'pointer-events', 'transform'].forEach((prop) => {
        drawer.style.removeProperty(prop);
      });
    }

    const rechargeHost = document.querySelector('#recharge-storefront-experiences__cart-drawer');
    const rechargeRoot = rechargeHost?.shadowRoot;
    if (rechargeRoot && rootLooksLikeCart(rechargeRoot)) {
      queryAllDeep('[role="dialog"],[aria-modal="true"],aside,[class*="drawer" i],[class*="Drawer" i],[class*="cart" i]', rechargeRoot)
        .filter((el) => rootLooksLikeCart(el) || rootHasVisibleCart(el))
        .forEach(hideElementForCartClose);
      hideElementForCartClose(rechargeHost);
    }

    setInactiveRechargeHostPassthrough();
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('overflow-hidden');
  }

  function isCartCloseClick(event) {
    if (getEventMatch(event, '[data-solinia-recharge-close]')) return false;
    const control = getEventMatch(event, 'button,a,[role="button"],[aria-label],[title],[data-testid],[class]');
    if (!control) return false;

    const label = [
      control.getAttribute?.('aria-label'),
      control.getAttribute?.('title'),
      control.getAttribute?.('data-testid'),
      control.getAttribute?.('class'),
      text(control),
    ].filter(Boolean).join(' ');
    if (!/(^|\b)(close|dismiss|cart[-_ ]?close|drawer[-_ ]?close)(\b|$)|^[×x]$/i.test(label)) return false;

    const path = event.composedPath?.() || [];
    return path.some((node) => rootLooksLikeCart(node)) ||
      rootLooksLikeCart(document.querySelector('cart-drawer')) ||
      rootLooksLikeCart(document.querySelector('#recharge-storefront-experiences__cart-drawer')?.shadowRoot);
  }

  document.addEventListener('click', (event) => {
    if (isCartCloseClick(event)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      forceCloseCartDrawer();
      return;
    }

    const lpCartTrigger = getEventMatch(event, '[data-lp-cart-open]');
    if (lpCartTrigger) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openThemeCartDrawer(lpCartTrigger);
      return;
    }

    const button = getEventMatch(event, 'button,a,[role="button"]');
    if (!button || !/Upgrade to subscription/i.test(text(button))) return;
    const cart = lastCart;
    const visibleLegacyCopy = /LP \d+ Jar Offer/i.test(text(document.body));
    if (!visibleLegacyCopy && !(cart?.items || []).some(isLegacyLpLine)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Preparing subscription...';
    getCart()
      .then(normalizeLegacyLpMagnesiumLines)
      .then(() => {
        lastCart = null;
        queueApply();
      })
      .finally(() => {
        button.removeAttribute('aria-busy');
      });
  }, true);

  document.addEventListener('click', (event) => {
    if (getEventMatch(event, '[data-solinia-recharge-info]')) {
      event.preventDefault();
      openInfoModal().catch(() => {});
      return;
    }
    if (getEventMatch(event, '[data-solinia-recharge-close]')) {
      event.preventDefault();
      closeInfoModal();
      return;
    }
    const modalAdd = getEventMatch(event, '[data-solinia-recharge-modal-add]');
    if (modalAdd) {
      event.preventDefault();
      addModalProduct(modalAdd).catch(() => {});
      return;
    }
    const customUpsellAdd = getEventMatch(event, '[data-solinia-custom-upsell-add]');
    if (customUpsellAdd) {
      event.preventDefault();
      addCustomUpsellProduct(customUpsellAdd).catch(() => {});
    }
  });

  document.addEventListener('keyup', (event) => {
    if (event.key === 'Escape') closeInfoModal();
  });

  const observer = new MutationObserver(queueApply);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  queueApply();
  window.addEventListener('load', queueApply);
  window.addEventListener('pageshow', queueApply);
  window.addEventListener('focus', queueApply);
  document.addEventListener('visibilitychange', queueApply);
  window.SoliniaRechargeCartOverridesVersion = VERSION;
  document.documentElement.dataset.soliniaRechargeCartOverrides = VERSION;
})();
